/**
 * /gift — Gift Subscription buy page.
 *
 * Logged-in user picks a plan, fills recipient + note, pays $X via
 * Stripe Checkout. On payment success they're redirected to /gift/sent
 * with the code they can hand to the recipient.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GiftIcon as Gift, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { ReaderAppBanner } from "@/components/ReaderAppBanner";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

const PLANS = [
  { key: "buyer_pass",    name: "Buyer Pass",     usd: 39,  desc: "12 months of unlimited inquiries, watchlists, video-tour discount." },
  { key: "dog_log",       name: "Dog Log",        usd: 69,  desc: "12 months of care tracking, vet PDFs, sitter portal." },
  { key: "breeder_basic", name: "Breeder Basic",  usd: 190, desc: "12 months — 10 dogs/4 litters per month, free single listings." },
  { key: "breeder_pro",   name: "Breeder Pro",    usd: 420, desc: "12 months — unlimited everything + Dog Log bundled free." },
];

export default function GiftPage() {
  // App Store 3.1.1 — no gift-subscription purchases in the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return <GiftPageWeb />;
}

function GiftPageWeb() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("buyer_pass");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) { navigate("/login?next=/gift"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/gift/purchase", {
        plan,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim().toLowerCase() || null,
        sender_name: senderName.trim() || null,
        note: note.trim(),
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  const selectedPlan = PLANS.find((p) => p.key === plan);
  return (
    <Shell>
      <SeoHead
        title="Gift a subscription · Armstrong K9 Registry"
        description="Gift a Buyer Pass, Dog Log, Breeder Basic, or Breeder Pro subscription to a friend. 12 months, redeemable with a code."
        url={`${webOrigin()}/gift`}
        type="website"
      />
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="gift-page">
        <ReaderAppBanner />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Gift a subscription
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] flex items-center gap-3">
          <Gift size={32} weight="duotone" className="text-gold" />
          12 months, on you.
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Pick a plan, fill in the recipient's name (email optional), pay once.
          We email them the redemption code, or you hand it to them yourself.
        </p>

        <div className="mt-8 space-y-3">
          {PLANS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlan(p.key)}
              className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
                plan === p.key ? "border-[#C9A227] bg-[#FBF7EC]" : "border-[#EAEAEA] bg-white hover:border-gold/55"
              }`}
              data-testid={`gift-plan-${p.key}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-ink">{p.name}</p>
                  <p className="text-[12.5px] text-[#6B6B6B] mt-0.5">{p.desc}</p>
                </div>
                <div className="text-right">
                  <p className="font-spectral text-2xl">${p.usd}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B]">12 months</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Recipient name</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="mt-1.5" data-testid="gift-recipient-name"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Recipient email (optional)</Label>
              <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="mt-1.5" placeholder="we'll email them the code" data-testid="gift-recipient-email"/>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Sender name (optional)</Label>
            <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} className="mt-1.5" placeholder={user?.name || "Your name"} data-testid="gift-sender-name"/>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className="mt-1.5 min-h-[100px]" placeholder="Happy birthday — for that new pup of yours!" data-testid="gift-note"/>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <Link to="/pricing" className="text-[12px] text-[#6B6B6B] hover:text-crimson underline">← Back to pricing</Link>
          <Button
            onClick={submit}
            disabled={busy || !recipientName.trim()}
            className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-6"
            data-testid="gift-submit"
          >
            {busy ? "Redirecting…" : `Pay $${selectedPlan?.usd} & send`}
          </Button>
        </div>
      </div>
    </Shell>
  );
}

export function GiftSentPage() {
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const { user } = useAuth();
  const Shell = user ? AppShell : PublicShell;
  return (
    <Shell>
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="gift-sent-page">
        <CheckCircle size={48} weight="fill" className="text-[#0F8043] mx-auto mb-4"/>
        <h1 className="font-spectral text-3xl tracking-tight">Gift sent.</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-md mx-auto leading-relaxed">
          If you added an email, the recipient just got the redemption code.
          Either way, save this for safekeeping:
        </p>
        <div className="mt-6 rounded-xl border-2 border-[#C9A227] bg-[#FBF7EC] p-5 inline-block">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-1">Redemption code</p>
          <p className="font-tabular text-2xl font-bold text-ink select-all" data-testid="gift-sent-code">{code}</p>
        </div>
        <p className="text-[11.5px] text-[#6B6B6B] mt-6">
          Redeem at <code className="font-tabular">/gift/redeem/{code}</code>
        </p>
      </div>
    </Shell>
  );
}
